import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/authcontext";
import { ProposalService, ProposalRequest } from "./proposal.service";
import {
  showConfirmationAlert,
  showSuccessAlert,
  showErrorAlert,
} from "../../uiInterface/base-ui/alertservice";
import ProposalForm from "./proposalForm";
import { ProposalFormDataProvider } from "./proposalFormDataContext";
import male from "../../../../src/assets/male.png";
import TableFilters, {
  FilterState,
  FilterConfig,
  mergeTableFilterChanges,
} from "../../../components/filters/TableFilters";
import LifecycleFilterDropdown from "../components/LifecycleFilterDropdown";
import SortableHeader from "../../../components/filters/SortableHeader";
import SortByDropdown from "../../../components/filters/SortByDropdown";
import ListFilterChips, {
  getClearedListFilters,
} from "../components/ListFilterChips";
import TablePagination from "../../../components/pagination/TablePagination";
import { useTenantChange } from "../../../hooks/useTenantChange";
import ExportComponent from "../../../components/ExportComponent";
import CollapseHeader from "../../../core/common/collapse-header";
import Table from "../../../core/common/dataTable/index";
import ManageColumns from "../../../components/filters/ManageColumns";
import { Modal, Tooltip, OverlayTrigger } from "react-bootstrap";
import { API_BASE_URL } from "../../../environment";
import { getAuthHeaders } from "../../../utils/authUtils";
import { getStageColor } from "../../../utils/stageColors";
import { sortRecords } from "../../../utils/sortUtils";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import {
  getLifecycleTypeName,
  getLifecycleTypePluralName,
} from "../../../utils/lifecycleUtils";
import ContractForm from "../contracts/contractForm";
import { ContractFormDataProvider } from "../contracts/contractFormDataContext";
import {
  ContractService,
  ContractRequest,
} from "../contracts/contract.service";
import EntityDetailsModal, {
  EntityDetailSection,
  EntityDetailLink,
} from "../../../components/EntityDetailsModal/EntityDetailsModal";
import { openOffcanvas } from "../../../utils/offcanvasUtils";
import { getApiErrorMessage } from "../../../utils/apiErrorUtils";

interface ProposalsProps {
  proposals: any[];
  loading: boolean;
  backgroundLoading?: boolean;
  error: string | null;
  pagination: any;
  fetchProposals: (
    page?: number,
    limit?: number,
    filterParams?: Record<string, any>
  ) => Promise<void>;
  refreshProposals: () => Promise<void>;
  addProposal: (proposal: any) => void;
  updateProposal: (proposal: any) => void;
  removeProposal: (proposalId: number) => void;
  lastFetched: Date | null;
  isInitialized: boolean;
  currentView: "list" | "grid";
  onViewSwitch: (view: "list" | "grid") => void;
  clearBackgroundLoading?: () => void;
}

const Proposals = ({
  proposals,
  loading,
  backgroundLoading = false,
  error,
  pagination,
  fetchProposals,
  refreshProposals,
  addProposal,
  updateProposal,
  removeProposal,
  lastFetched,
  isInitialized,
  currentView,
  onViewSwitch,
  clearBackgroundLoading,
  ...rest
}: ProposalsProps) => {
  const navigate = useNavigate();
  const { selectedTenantId } = useContext(AuthContext);
  const [tenantUsers, setTenantUsers] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);

  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    dateRange: [null, null] as [Date | null, Date | null],
    industryFilter: [],
    countryFilter: [],
    statusFilter: null,
    sortBy: "created_datetime",
    sortOrder: "desc",
    archivedFilter: "no",
  });

  const [currentPage, setCurrentPage] = useState(pagination.currentPage || 1);
  const [pageSize, setPageSize] = useState(pagination.pageSize || 12);

  const [addProposalLoading, setAddProposalLoading] = useState(false);
  const [addProposalError, setAddProposalError] = useState<string | null>(null);
  const [selectedProposal, setSelectedProposal] = useState<any | null>(null);
  const [detailsProposal, setDetailsProposal] = useState<any | null>(null);
  const [isAddProposalOpen, setIsAddProposalOpen] = useState(false);
  const [editProposalLoading, setEditProposalLoading] = useState(false);
  const [editProposalError, setEditProposalError] = useState<string | null>(
    null
  );
  const [selectedProposalForDelete, setSelectedProposalForDelete] = useState<
    any | null
  >(null);
  const [deleteProposalLoading, setDeleteProposalLoading] = useState(false);
  const [deleteProposalError, setDeleteProposalError] = useState<string | null>(
    null
  );
  const [resetFormTrigger, setResetFormTrigger] = useState(0);
  const [stars, setStars] = useState<{ [key: number]: boolean }>({});
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const defaultColumnVisibility = {
    subject: true,
    amount: true,
    assigned_user: true,
    start_datetime: true,
    status: true,
    assigned_team: false,
    deal: false,
    currency_code: false,
    company: true,
  };

  const [columnVisibility, setColumnVisibility] = useState<{
    [key: string]: boolean;
  }>(() => {
    try {
      const saved = localStorage.getItem("proposals_column_visibility");
      if (saved) {
        const parsed = JSON.parse(saved);
        const visibility: { [key: string]: boolean } = {
          ...defaultColumnVisibility,
        };
        Object.keys(parsed).forEach((key) => {
          if (key in defaultColumnVisibility) {
            visibility[key] = parsed[key] === true;
          }
        });
        const visibleCount = Object.values(visibility).filter(Boolean).length;
        if (visibleCount === 0) {
          const defaultKeys = Object.keys(defaultColumnVisibility);
          for (const key of defaultKeys) {
            if (
              defaultColumnVisibility[
              key as keyof typeof defaultColumnVisibility
              ]
            ) {
              visibility[key] = true;
              break;
            }
          }
        }
        return visibility;
      }
    } catch (error) {
      console.error(
        "Error loading column visibility from localStorage:",
        error
      );
    }
    return defaultColumnVisibility;
  });

  const saveColumnVisibilityToStorage = useCallback(
    (visibility: { [key: string]: boolean }) => {
      try {
        localStorage.setItem(
          "proposals_column_visibility",
          JSON.stringify(visibility)
        );
      } catch (error) {
        console.error(
          "Error saving column visibility to localStorage:",
          error
        );
      }
    },
    []
  );

  const availableColumns = [
    { key: "subject", label: "Subject" },
    { key: "company", label: "Company" },
    { key: "amount", label: "Amount" },
    { key: "assigned_user", label: "Assigned User" },
    { key: "start_datetime", label: "Start Date" },
    { key: "status", label: "Status" },
    { key: "assigned_team", label: "Assigned Team" },
    { key: "deal", label: "Related Deal" },
    { key: "currency_code", label: "Currency" },

  ];

  const [orderedAvailableColumns, setOrderedAvailableColumns] = useState(() => {
    const storageKey = `${window.location.pathname}_column_order`;
    const savedOrder = localStorage.getItem(storageKey);
    if (savedOrder) {
      try {
        const orderKeys = JSON.parse(savedOrder);
        return [...availableColumns].sort((a, b) => {
          const indexA = orderKeys.indexOf(a.key);
          const indexB = orderKeys.indexOf(b.key);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      } catch (e) {
        return [...availableColumns];
      }
    }
    return [...availableColumns];
  });

  const getSortedAvailableColumns = () => {
    return orderedAvailableColumns;
  };

  const handleColumnVisibilityChange = (
    columnKey: string,
    isVisible: boolean
  ) => {
    const currentVisibleCount =
      Object.values(columnVisibility).filter(Boolean).length;
    if (isVisible && currentVisibleCount >= 6) {
      showErrorAlert(
        "Maximum 6 columns can be enabled at once. Please disable another column first."
      );
      return;
    }
    if (!isVisible && currentVisibleCount <= 1) {
      showErrorAlert("At least one column must remain visible.");
      return;
    }
    const newColumnVisibility = {
      ...columnVisibility,
      [columnKey]: isVisible,
    };
    setColumnVisibility(newColumnVisibility);
    saveColumnVisibilityToStorage(newColumnVisibility);
  };

  const handleResetColumns = () => {
    setColumnVisibility(defaultColumnVisibility);
    saveColumnVisibilityToStorage(defaultColumnVisibility);
  };

  const [downloadingProposals, setDownloadingProposals] = useState<{
    [key: number]: boolean;
  }>({});
  const [isCreatingContract, setIsCreatingContract] = useState(false);
  const [createContractError, setCreateContractError] = useState<string>("");
  const [resetContractFormTrigger, setResetContractFormTrigger] = useState(0);
  const [selectedProposalForContract, setSelectedProposalForContract] =
    useState<any | null>(null);

  const filterConfig: FilterConfig = useMemo(
    () => ({
      showSearch: true,
      searchField: "subject",
      showDateFilter: true,
      dateField: "created_datetime",
      showIndustryFilter: false,
      showCountryFilter: false,
      showStatusFilter: false,
      pageName: "proposal" as const,
      defaultSort: {
        field: "created_datetime",
        order: "desc" as const,
      },
    }),
    []
  );

  const handleFilterChange = useCallback(
    (newFilters: FilterState) => {
      const mergedFilters = mergeTableFilterChanges(filters, newFilters);
      setFilters(mergedFilters);
      setCurrentPage(1);
      fetchProposals(1, 12, mergedFilters);
    },
    [fetchProposals, filters]
  );

  const handleDropdownFilterChange = useCallback(
    (dropdownFilters: Record<string, any>) => {
      const updatedFilters = {
        ...filters,
        ...dropdownFilters,
      };
      setFilters(updatedFilters);
      setCurrentPage(1);
      fetchProposals(1, 12, updatedFilters);
    },
    [filters, fetchProposals]
  );

  const applyListFilters = useCallback(
    (nextFilters: FilterState) => {
      setFilters(nextFilters);
      setCurrentPage(1);
      fetchProposals(1, 12, nextFilters);
    },
    [fetchProposals]
  );

  const handleApiFilterChange = useCallback(
    (apiFilters: Record<string, any>) => { },
    []
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      fetchProposals(page, 12, filters);
    },
    [fetchProposals, filters]
  );

  const handleSortChange = useCallback(
    (newSortBy: string, newOrder: "asc" | "desc") => {
      const newFilters = { ...filters, sortBy: newSortBy, sortOrder: newOrder };
      setFilters(newFilters);
    },
    [filters]
  );

  useEffect(() => {
    if (backgroundLoading) {
      const fetchBackgroundData = async () => {
        try {
          const teamList = await ProposalService.getTeams(
            Number(selectedTenantId)
          );
          setTeams(teamList || []);
        } catch (e) {
          console.error("Background API error:", e);
        }
      };
      fetchBackgroundData();
    }
  }, [backgroundLoading, selectedTenantId]);

  React.useEffect(() => {
    const starsState: { [key: number]: boolean } = {};
    proposals.forEach((item, index) => {
      starsState[index] = false;
    });
    setStars(starsState);
  }, [proposals]);

  React.useEffect(() => {
    setStars({});
  }, [currentPage, filters.sortBy, filters.sortOrder]);

  const handleStarToggle = (index: number) => {
    setStars((prevStars) => ({ ...prevStars, [index]: !prevStars[index] }));
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setStars((prev) => {
      const newStars = { ...prev };
      paginatedProposals.forEach((_: any, idx: number) => {
        newStars[idx] = checked;
      });
      return newStars;
    });
  };

  const closeOffcanvas = (offcanvasId: string) => {
    const offcanvas = document.getElementById(offcanvasId);
    if (offcanvas) {
      try {
        if ((window as any).bootstrap?.Offcanvas) {
          const bsOffcanvas = (window as any).bootstrap.Offcanvas.getInstance(
            offcanvas
          );
          if (bsOffcanvas) {
            bsOffcanvas.hide();
            return;
          }
        }

        const closeEvent = new Event("click");
        const closeButton = offcanvas.querySelector(
          '[data-bs-dismiss="offcanvas"]'
        );
        if (closeButton) {
          closeButton.dispatchEvent(closeEvent);
          return;
        }

        offcanvas.classList.remove("show");
        document.body.classList.remove("offcanvas-open");
        const backdrop = document.querySelector(".offcanvas-backdrop");
        if (backdrop) {
          backdrop.remove();
        }
      } catch (error) {
        console.warn("Error closing offcanvas:", error);
      }
    }
  };

  useEffect(() => {
    saveColumnVisibilityToStorage(columnVisibility);
  }, [columnVisibility, saveColumnVisibilityToStorage]);

  useEffect(() => {
    const addOffcanvas = document.getElementById("offcanvas_add_proposal");

    const handleHidden = () => {
      setIsAddProposalOpen(false);
    };

    if (addOffcanvas) {
      addOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (addOffcanvas) {
        addOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);

  const handleAddProposal = async (proposalData: Partial<ProposalRequest>) => {
    setAddProposalLoading(true);
    setAddProposalError(null);
    try {
      const createdProposal = await ProposalService.createProposal(
        proposalData
      );
      closeOffcanvas("offcanvas_add_proposal");
      setTimeout(() => setResetFormTrigger((prev) => prev + 1), 100);
      setTimeout(
        () =>
          showSuccessAlert(
            `${getLifecycleTypeName("PROPOSAL")} created successfully!`
          ),
        200
      );
      return createdProposal;
    } catch (error: any) {
      console.error("Error creating proposal:", error);
      setAddProposalError(getApiErrorMessage(error, `Failed to create ${getLifecycleTypeName("PROPOSAL").toLowerCase()}`));
      showErrorAlert(getApiErrorMessage(error, `Failed to create ${getLifecycleTypeName("PROPOSAL").toLowerCase()}`));
      throw error;
    } finally {
      setAddProposalLoading(false);
    }
  };

  const handleEditProposal = async (proposalData: Partial<ProposalRequest>) => {
    if (!selectedProposal) return;
    setEditProposalLoading(true);
    setEditProposalError(null);
    try {
      await ProposalService.updateProposal(
        selectedProposal.proposal_id,
        proposalData
      );
      closeOffcanvas("offcanvas_edit");
      setSelectedProposal(null);
      fetchProposals(currentPage, 12, filters);
      setTimeout(
        () =>
          showSuccessAlert(
            `${getLifecycleTypeName("PROPOSAL")} updated successfully!`
          ),
        200
      );
    } catch (error: any) {
      console.error("Error updating proposal:", error);
      setEditProposalError(getApiErrorMessage(error, `Failed to update ${getLifecycleTypeName("PROPOSAL").toLowerCase()}`));
      showErrorAlert(getApiErrorMessage(error, `Failed to update ${getLifecycleTypeName("PROPOSAL").toLowerCase()}`));
      throw error;
    } finally {
      setEditProposalLoading(false);
    }
  };

  const handleDeleteProposal = async (proposal: any) => {
    if (!selectedTenantId) return;

    const result = await showConfirmationAlert(
      `Delete ${getLifecycleTypeName("PROPOSAL")}`,
      `Are you sure you want to delete "${proposal.subject}"? This action cannot be undone.`,
      async () => {
        await ProposalService.deleteProposal(
          proposal.proposal_id,
          Number(selectedTenantId)
        );
      }
    );

    if (result.isConfirmed) {
      await showSuccessAlert(
        `${getLifecycleTypeName("PROPOSAL")} deleted successfully!`
      );
      fetchProposals(currentPage, 12, filters);
    }
  };

  const handleBulkDelete = async () => {
    const selectedIndices = Object.keys(stars)
      .filter((key) => stars[parseInt(key)])
      .map((key) => parseInt(key));
    if (selectedIndices.length === 0) return;

    const result = await showConfirmationAlert(
      "Delete Selected Records",
      `Are you sure you want to delete ${selectedIndices.length} selected record${selectedIndices.length > 1 ? "s" : ""}? This action cannot be undone.`,
      async () => {
        setIsBulkDeleting(true);
        try {
          const selectedProposals = selectedIndices
            .map((index) => paginatedProposals[index])
            .filter((proposal) => proposal);
          const deletePromises = selectedProposals.map((proposal) =>
            ProposalService.deleteProposal(
              proposal.proposal_id,
              Number(selectedTenantId)
            )
          );
          await Promise.all(deletePromises);
        } finally {
          setIsBulkDeleting(false);
        }
      }
    );

    if (result.isConfirmed && result.value) {
      setStars({});
      await showSuccessAlert(
        `${selectedIndices.length} ${selectedIndices.length > 1
          ? getLifecycleTypePluralName("PROPOSAL").toLowerCase()
          : getLifecycleTypeName("PROPOSAL").toLowerCase()
        } deleted successfully!`
      );
      fetchProposals(currentPage, 12, filters);
    }
  };;

  const handleEditClick = (proposal: any) => {
    flushSync(() => {
      setSelectedProposal(proposal);
      setEditProposalError(null);
    });
  };



  const handlePreviewClick = (proposal: any) => {
    window.location.href = `/proposals/${proposal.proposal_id}`;
  };

  const handleDownloadProposal = async (proposal: any) => {
    if (!proposal?.proposal_id || !selectedTenantId) return;

    const proposalId = proposal.proposal_id;

    setDownloadingProposals((prev) => ({ ...prev, [proposalId]: true }));

    try {
      const headers = getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/proposal/${proposalId}/generate_pdf_link/?tenant_id=${selectedTenantId}`,
        {
          method: "GET",
          headers: headers,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.download_url) {
        const pdfResponse = await fetch(result.download_url);
        if (!pdfResponse.ok) {
          throw new Error("Failed to fetch PDF file");
        }

        const pdfBlob = await pdfResponse.blob();

        const blobUrl = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = result.filename || `proposal_${proposalId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(blobUrl);

        showSuccessAlert(
          `${getLifecycleTypeName("PROPOSAL")} PDF downloaded successfully!`
        );
      } else {
        throw new Error(
          result.message || "Failed to generate PDF download link"
        );
      }
    } catch (error: any) {
      console.error("Error downloading proposal:", error);
      showErrorAlert(
        error.message ||
          `Failed to download ${getLifecycleTypeName("PROPOSAL").toLowerCase()} PDF`
      );
    } finally {
      setDownloadingProposals((prev) => {
        const newState = { ...prev };
        delete newState[proposalId];
        return newState;
      });
    }
  };

  const handleCloseCreateContract = () => {
    setCreateContractError("");
    setSelectedProposalForContract(null);
    setResetContractFormTrigger((prev) => prev + 1);
    closeOffcanvas("offcanvas_create_contract");
  };

  const handleCreateContractSubmit = async (
    formData: Partial<ContractRequest>
  ) => {
    if (isCreatingContract) return;

    setIsCreatingContract(true);
    setCreateContractError("");

    try {
      const newContract = await ContractService.createContract(formData as any);
      closeOffcanvas("offcanvas_create_contract");
      setResetContractFormTrigger((prev) => prev + 1);
      setSelectedProposalForContract(null);

      setTimeout(() => {
        showSuccessAlert(
          `${getLifecycleTypeName("CONTRACT")} "${newContract.subject}" created successfully!`
        );
      }, 500);

      setTimeout(() => {
        navigate(`/contracts/${newContract.contract_id}`);
      }, 1000);

      return newContract;
    } catch (error: any) {
      console.error("Error creating contract:", error);
      setCreateContractError(getApiErrorMessage(error, `Failed to create ${getLifecycleTypeName("CONTRACT").toLowerCase()}. Please try again.`));
      showErrorAlert(getApiErrorMessage(error, `Failed to create ${getLifecycleTypeName("CONTRACT").toLowerCase()}. Please try again.`));
      throw error;
    } finally {
      setIsCreatingContract(false);
    }
  };

  const getPrefilledContractData = (proposal: any) => {
    if (!proposal) return {};

    return {
      subject: proposal.subject || "",
      description:
        proposal.description ||
        `${getLifecycleTypeName("CONTRACT")} for ${proposal.subject || getLifecycleTypeName("PROPOSAL")}`,
      amount: proposal.amount || undefined,
      currency_code: proposal.currency_code || "USD",
      company_id: proposal.company?.company_id || null,
      proposal_id: proposal.proposal_id || null,
      product_n_services: proposal.product_n_services || "",
      assigned_user_id: proposal.assigned_user?.user_id || null,
      assigned_team_id: proposal.assigned_team?.team_id || null,
      due_datetime: proposal.start_datetime || "",
      expiration_datetime: proposal.expiration_datetime || "",
      existingProductsServices: (proposal as any).product_n_service || [],
      existingAttachments: (proposal as any).attachments || [],
      company: proposal.company,
    };
  };

  const handleConvertToContract = (proposal: any) => {
    setSelectedProposalForContract(proposal);
    setCreateContractError("");
  };

  const paginatedProposals = useMemo(
    () =>
      sortRecords<any>(
        Array.isArray(proposals) ? proposals : [],
        filters.sortBy,
        filters.sortOrder,
        {
          assigned_team: (record) =>
            record.assigned_team?.team_name ||
            teams.find((t) => t.team_id === record.assigned_team_id)?.team_name,
        }
      ),
    [proposals, filters.sortBy, filters.sortOrder, teams]
  );

  const userOptions = tenantUsers.map((u) => ({
    value: u.user_id,
    label: `${u.user_first_name.trim()} ${u.user_last_name.trim()}`,
  }));

  const allAvailableColumns = useMemo(
    () => [
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title={`${getLifecycleTypeName("PROPOSAL")} Subject`}
              field="subject"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "subject",
        key: "subject",
        render: (text: string, record: any) => (
          <div>
            <div>
              <Link
                to={`/proposals/${record.proposal_id}`}
                state={{ proposalData: record }}
                className="title-name"
              >
                {record.subject?.length > 40 ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-${record.proposal_id}`}>{record.subject}</Tooltip>}
                  >
                    <span>{`${record.subject.substring(0, 40)}...`}</span>
                  </OverlayTrigger>
                ) : (
                  record.subject
                )}
              </Link>
            </div>
            <span className="text-muted small">
              {record.company?.company_name || "-"}
            </span>
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Company"
              field="company"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "company",
        key: "company",
        render: (company: any, record: any) => (
          <div className={!record.company?.company_name ? "text-center fw-bold" : ""}>
            {record.company?.company_name || "-"}
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Amount"
              field="grand_total"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "amount",
        key: "amount",
        render: (text: string, record: any) => (
          <>
            {record.grand_total ? (
              <span className="text-dark">
                {record.currency_code}{" "}
                {Number(record.grand_total || 0).toFixed(4)}
              </span>
            ) : (
              <div className="text-center fw-bold">-</div>
            )}
          </>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Assigned Team"
              field="assigned_team"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "assigned_team",
        key: "assigned_team",
        render: (text: string, record: any) => (
          <>
            {record.assigned_team?.team_name ||
              (() => {
                const team = teams.find(
                  (t) => t.team_id === record.assigned_team_id
                );
                return team ? team.team_name : "No Team Assigned";
              })()}
          </>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Assigned User"
              field="assigned_user"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "assigned_user",
        key: "assigned_user",
        render: (text: string, record: any) => (
          <>
            {record.assigned_user &&
              Object.keys(record.assigned_user).length > 0 &&
              record.assigned_user.user_id ? (
              <div className="d-flex align-items-center">
                {record.assigned_user.avatar_url ? (
                  <img
                    src={record.assigned_user.avatar_url}
                    alt={record.assigned_user.full_name}
                    className="me-2 rounded-circle"
                    style={{ width: 24, height: 24, objectFit: "cover" }}
                  />
                ) : (
                  <img
                    src={male}
                    alt="Default Avatar"
                    className="me-2 rounded-circle"
                    style={{ width: 24, height: 24, objectFit: "cover" }}
                  />
                )}
                <span>{record.assigned_user.full_name}</span>
              </div>
            ) : (
              "No User Assigned"
            )}
          </>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title={`Related ${getLifecycleTypeName("DEAL")}`}
              field="deal"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "deal",
        key: "deal",
        render: (text: string, record: any) => (
          <>
            {record.deal?.deal_id ? (
              <Link
                to={`/deals/${record.deal.deal_id}`}
                className="crm-link"
                target="_blank"
              >
                {record.deal.title?.length > 40 ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-deal-${record.deal.deal_id}`}>{record.deal.title}</Tooltip>}
                  >
                    <span>{`${record.deal.title.substring(0, 40)}...`}</span>
                  </OverlayTrigger>
                ) : (
                  record.deal.title || "-"
                )}
              </Link>
            ) : record.deal?.title ? (
              record.deal.title?.length > 40 ? (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id={`tooltip-deal-title-${record.proposal_id}`}>{record.deal.title}</Tooltip>}
                >
                  <span>{`${record.deal.title.substring(0, 40)}...`}</span>
                </OverlayTrigger>
              ) : (
                record.deal.title
              )
            ) : (
              <div className="text-center fw-bold">-</div>
            )}
          </>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Start Date"
              field="start_datetime"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "start_datetime",
        key: "start_datetime",
        render: (text: string, record: any) => (
          <>
            {record.start_datetime ? (
              <span className="text-dark">
                {formatCustomDateTime(record.start_datetime)}
              </span>
            ) : (
              <div className="text-center fw-bold">-</div>
            )}
          </>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title={`${getLifecycleTypeName("PROPOSAL")} Status`}
              field="proposal_status"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "status",
        key: "status",
        render: (text: string, record: any) => (
          <span
            className={`badge badge-pill badge-status ${getStageColor(
              record.proposal_status?.proposal_status_display_name,
              record.proposal_status?.proposal_status_id,
              record.proposal_status?.proposal_status_order
            )} no-border`}
            style={{ border: "none", borderColor: "transparent" }}
          >
            {record.proposal_status?.proposal_status_display_name || "-"}
          </span>
        ),
      },
      {
        title: (
          <SortableHeader
            title="Currency"
            field="currency_code"
            currentSort={filters.sortBy}
            currentOrder={filters.sortOrder}
            onSort={handleSortChange}
          />
        ),
        dataIndex: "currency_code",
        key: "currency_code",
        render: (text: string, record: any) => (
          <div className={!record.currency_code ? "text-center fw-bold" : ""}>{record.currency_code || "-"}</div>
        ),
      },
    ],
    [filters.sortBy, filters.sortOrder, handleSortChange, teams]
  );

  const buildProposalSections = useCallback(
    (proposal: any): EntityDetailSection[] => {
      return [
        {
          title: `${getLifecycleTypeName("PROPOSAL")} Info`,
          icon: "ti ti-file-text",
          fields: [
            { label: "Subject", value: proposal.subject, fullWidth: true },
            {
              label: "Status",
              value:
                proposal.proposal_status?.proposal_status_display_name,
            },
            {
              label: "Start Date",
              value: proposal.start_datetime
                ? formatCustomDateTime(proposal.start_datetime)
                : null,
            },
            {
              label: "Expiration Date",
              value: proposal.expiration_datetime
                ? formatCustomDateTime(proposal.expiration_datetime)
                : null,
            },
          ],
        },
        {
          title: "Company & Deal",
          icon: "ti ti-building",
          fields: [
            { label: "Company", value: proposal.company?.company_name },
            {
              label: `Related ${getLifecycleTypeName("DEAL")}`,
              value: proposal.deal?.deal_id ? (
                <EntityDetailLink
                  href={`/deals/${proposal.deal.deal_id}`}
                  label={proposal.deal.title}
                />
              ) : (
                proposal.deal?.title
              ),
            },
          ],
        },
        {
          title: "Ownership",
          icon: "ti ti-users",
          fields: [
            {
              label: "Assigned Team",
              value: proposal.assigned_team?.team_name,
            },
            {
              label: "Assigned User",
              value: proposal.assigned_user?.full_name,
            },
          ],
        },
        {
          title: "Financials",
          icon: "ti ti-currency-dollar",
          fields: [
            {
              label: "Amount",
              value: proposal.grand_total
                ? `${proposal.currency_code || "$"} ${Number(
                  proposal.grand_total || 0
                ).toFixed(4)}`
                : null,
            },
            { label: "Currency", value: proposal.currency_code },
          ],
        },
        {
          title: "Timestamps",
          icon: "ti ti-clock",
          fields: [
            {
              label: "Created",
              value: proposal.created_datetime
                ? formatCustomDateTime(proposal.created_datetime)
                : null,
            },
            {
              label: "Updated",
              value: proposal.updated_datetime
                ? formatCustomDateTime(proposal.updated_datetime)
                : null,
            },
          ],
        },
      ];
    },
    []
  );

  const columns = useMemo(
    () => [
      {
        title: (
          <div className="form-check form-check-md">
            <input
              className="form-check-input"
              type="checkbox"
              checked={
                !!paginatedProposals.every(
                  (_: any, idx: number) => stars[idx]
                )
              }
              onChange={handleSelectAll}
            />
          </div>
        ),
        dataIndex: "selection",
        render: (_: any, record: any, index: number) => (
          <div className="form-check form-check-md">
            <input
              className="form-check-input"
              type="checkbox"
              checked={!!stars[index]}
              onChange={() => handleStarToggle(index)}
            />
          </div>
        ),
      },
      ...getSortedAvailableColumns()
        .filter((col) => columnVisibility[col.key])
        .map((col) =>
          allAvailableColumns.find((allCol) => allCol.key === col.key)
        )
        .filter(Boolean),
      {
        title: "Actions",
        dataIndex: "actions",
        render: (text: string, record: any) => {
          const isDownloadEnabled =
            record.approval_status === "approved" ||
            record.approval_status === "not_initiated_yet" ||
            !record.approval_status;

          return (
            <div className="d-flex align-items-center justify-content-end gap-2 position-static">
              <Link
                to="#"
                className="action-icon"
                onClick={(e) => {
                  e.preventDefault();
                  setDetailsProposal(record);
                }}
              >
                <i className="ti ti-eye text-blue-light" />
              </Link>
              <div className="dropdown table-action position-static">
                <Link
                  to="#"
                  className="action-icon"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  data-bs-boundary="viewport"
                  data-bs-popper-config='{"strategy":"fixed"}'
                >
                  <i className="fa fa-ellipsis-v" />
                </Link>
                <div className="dropdown-menu dropdown-menu-end">
                  <Link
                    className="dropdown-item"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePreviewClick(record);
                    }}
                  >
                    <i className="ti ti-info-circle text-blue-light" /> View Details
                  </Link>
                  <Link
                    className="dropdown-item edit-popup"
                    to="#"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#offcanvas_edit"
                    onClick={() => handleEditClick(record)}
                  >
                    <i className="ti ti-edit text-blue" /> Edit
                  </Link>
                  <Link
                    className="dropdown-item"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleDeleteProposal(record);
                    }}
                  >
                    <i className="ti ti-trash text-danger" /> Delete
                  </Link>
                  <Link
                    className="dropdown-item"
                    to="#"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#offcanvas_create_contract"
                    onClick={(e) => {
                      e.preventDefault();
                      handleConvertToContract(record);
                    }}
                  >
                    <i className="ti ti-file-certificate text-blue" /> Convert to{" "}
                    {getLifecycleTypeName("CONTRACT")}
                  </Link>
                  <Link
                    className={`dropdown-item ${downloadingProposals[record.proposal_id] ||
                      !isDownloadEnabled
                      ? "disabled"
                      : ""
                      }`}
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (
                        !downloadingProposals[record.proposal_id] &&
                        isDownloadEnabled
                      ) {
                        handleDownloadProposal(record);
                      }
                    }}
                    style={{
                      opacity:
                        downloadingProposals[record.proposal_id] ||
                          !isDownloadEnabled
                          ? 0.6
                          : 1,
                      pointerEvents: downloadingProposals[record.proposal_id]
                        ? "none"
                        : "auto",
                      cursor: !isDownloadEnabled ? "not-allowed" : "pointer",
                    }}
                  >
                    {downloadingProposals[record.proposal_id] ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-file-download text-blue-light me-2"></i>
                        Download {getLifecycleTypeName("PROPOSAL")}
                      </>
                    )}
                  </Link>
                </div>
              </div>
            </div>
          );
        },
      },
    ],
    [
      columnVisibility,
      paginatedProposals,
      stars,
      handleSelectAll,
      handleStarToggle,
      getSortedAvailableColumns,
      allAvailableColumns,
      handleEditClick,
      handleDeleteProposal,
      handlePreviewClick,
      handleConvertToContract,
      handleDownloadProposal,
      downloadingProposals,
    ]
  );

  return (
    <ProposalFormDataProvider isMainApiCompleted={!loading} shouldLoad={isAddProposalOpen || !!selectedProposal}>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">
                      {getLifecycleTypePluralName("PROPOSAL")}
                      <span className="count-title">
                        {pagination.totalCount || 0}
                      </span>
                    </h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col">
                      <ListFilterChips
                        entityType="proposal"
                        filters={filters}
                        onRemove={(patch) => applyListFilters({ ...filters, ...patch })}
                        onClearAll={() => applyListFilters(getClearedListFilters())}
                        disabled={loading}
                        className="mb-0"
                      />
                    </div>
                    <div className="col-sm-auto">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        {Object.values(stars).some(Boolean) && (
                          <button
                            type="button"
                            className="btn btn-danger ms-3"
                            onClick={handleBulkDelete}
                            disabled={isBulkDeleting}
                          >
                            <i className="ti ti-trash me-2" />
                            Delete Selected (
                            {Object.values(stars).filter(Boolean).length})
                          </button>
                        )}
                        &nbsp;&nbsp;
                        <ExportComponent entityType="proposal" filters={filters} />
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_add_proposal"
                          onClick={() => setIsAddProposalOpen(true)}
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add {getLifecycleTypeName("PROPOSAL")}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <TableFilters
                      config={filterConfig}
                      data={proposals}
                      onFilterChange={handleFilterChange}
                      onApiFilterChange={handleApiFilterChange}
                      sortBy={filters.sort_by}
                      searchTerm={filters.searchTerm}
                      dateRange={filters.dateRange}
                      hideClearButton
                      tenantId={selectedTenantId || undefined}
                      className="mt-4"
                    />
                  </div>
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <SortByDropdown
                      value={filters.sort_by}
                      onChange={(sortBy) =>
                        setFilters((prev) => ({ ...prev, sort_by: sortBy }))
                      }
                    />
                    <span className="mx-2">
                      <LifecycleFilterDropdown
                        entityType="proposal"
                        onFilterChange={handleDropdownFilterChange}
                        currentFilters={{ ...filters }}
                        loading={loading}
                      />
                    </span>
                    <ManageColumns
                      availableColumns={availableColumns}
                      columnVisibility={columnVisibility}
                      onColumnVisibilityChange={handleColumnVisibilityChange}
                      onResetColumns={handleResetColumns}
                      onReorderColumns={setOrderedAvailableColumns}
                    />
                    <div className="view-icons ms-3">
                      <Link to="#" className="active">
                        <i className="ti ti-list-tree" />
                      </Link>
                      <Link
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          onViewSwitch?.("grid");
                        }}
                      >
                        <i className="ti ti-grid-dots" />
                      </Link>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="card-body">
                    <div
                      className="d-flex flex-column align-items-center justify-content-center"
                      style={{ minHeight: "60vh" }}
                    >
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="mt-2">
                        <small className="text-muted">
                          Loading {getLifecycleTypePluralName("PROPOSAL")}...
                        </small>
                      </div>
                    </div>
                  </div>
                ) : error ? (
                  <div className="card-body">
                    <div className="alert alert-danger text-center">
                      <i className="ti ti-alert-circle me-2"></i>
                      {error}
                    </div>
                  </div>
                ) : (
                  <div className="card-body">
                    <div
                      className="table-responsive contact-table-wrapper"
                      style={{ position: "relative" }}
                    >
                      {paginatedProposals.length === 0 ? (
                        <div className="text-center py-5">
                          <div className="avatar avatar-xl bg-light-gray rounded-circle mb-3 mx-auto">
                            <i className="ti ti-file-text fs-36 text-gray"></i>
                          </div>
                          <h4 className="text-gray mb-2">
                            No {getLifecycleTypePluralName("PROPOSAL")} Found!
                          </h4>
                          <p className="text-muted mb-3">
                            There are no{" "}
                            {getLifecycleTypePluralName("PROPOSAL").toLowerCase()}{" "}
                            to display at the moment.
                          </p>
                          <button
                            className="btn btn-primary"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#offcanvas_add_proposal"
                            onClick={() => setIsAddProposalOpen(true)}
                          >
                            <i className="fa fa-plus me-2" />
                            Add Your First {getLifecycleTypeName("PROPOSAL")}
                          </button>
                        </div>
                      ) : (
                        <>
                          <Table
                            columns={columns}
                            dataSource={paginatedProposals}
                            rowKey={(record: any) =>
                              record.proposal_id || record.id
                            }
                            rowSelection={false}
                            className="table table-striped contact-table"
                            pagination={false}
                          />
                          <TablePagination
                            currentPage={pagination.currentPage || currentPage}
                            pageSize={12}
                            totalItems={pagination.totalCount}
                            filteredItems={pagination.totalCount}
                            isFiltered={
                              filters.searchTerm !== "" ||
                              filters.dateRange[0] !== null ||
                              filters.dateRange[1] !== null
                            }
                            onPageChange={handlePageChange}
                            onPageSizeChange={(
                              current: number,
                              size: number
                            ) => {
                              setCurrentPage(1);
                              setPageSize(size);
                              setStars({});
                              fetchProposals(1, size, filters);
                            }}
                            entityName={getLifecycleTypePluralName("PROPOSAL")}
                            loading={loading}
                          />
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add New Proposals Off-canvas */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvas_add_proposal"
        style={{ width: "550px" }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">
            Add New {getLifecycleTypeName("PROPOSAL")}
          </h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {addProposalError && (
            <div className="alert alert-danger" role="alert">
              {addProposalError}
            </div>
          )}
          {isAddProposalOpen && (
            <ProposalForm
              key={`create-new-proposal-${resetFormTrigger}`}
              onSubmit={handleAddProposal}
              onCancel={() => closeOffcanvas("offcanvas_add_proposal")}
              isSubmitting={addProposalLoading}
              resetTrigger={resetFormTrigger}
              onRefreshList={() => fetchProposals(currentPage, pageSize, filters)}
              formId="add-proposal"
            />
          )}
        </div>
      </div>

      {/* Edit Proposals */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvas_edit"
        style={{ width: "550px" }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">
            Edit {getLifecycleTypeName("PROPOSAL")}
          </h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {editProposalError && (
            <div className="alert alert-danger" role="alert">
              {editProposalError}
            </div>
          )}
          {selectedProposal && (
            <ProposalFormDataProvider isMainApiCompleted={true}>
              <ProposalForm
                proposal={selectedProposal}
                onSubmit={handleEditProposal}
                isSubmitting={editProposalLoading}
                onCancel={() => {
                  setSelectedProposal(null);
                  closeOffcanvas("offcanvas_edit");
                }}
                isMainApiCompleted={true}
                formId="edit-proposal"
              />
            </ProposalFormDataProvider>
          )}
        </div>
      </div>

      {/* Delete Proposal Modal Removed */}

      {/* Bulk Delete Confirmation Modal */}

      {detailsProposal && (
        <EntityDetailsModal
          isOpen={!!detailsProposal}
          onClose={() => setDetailsProposal(null)}
          title={detailsProposal.subject}
          subtitle={detailsProposal.company?.company_name || undefined}
          eyebrow={getLifecycleTypeName("PROPOSAL")}
          avatarIcon="ti ti-file-text"
          badge={{
            label:
              detailsProposal.proposal_status
                ?.proposal_status_display_name || "No Status",
            tone: "info",
          }}
          sections={buildProposalSections(detailsProposal)}
          onEdit={() => {
            const proposal = detailsProposal;
            setDetailsProposal(null);
            handleEditClick(proposal);
            openOffcanvas("offcanvas_edit");
          }}
        />
      )}

      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvas_create_contract"
        style={{ width: "550px" }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Create {getLifecycleTypeName("CONTRACT")}</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {createContractError && (
            <div className="alert alert-danger" role="alert">
              {createContractError}
            </div>
          )}
          {selectedProposalForContract && (
            <ContractFormDataProvider isMainApiCompleted={true}>
              <ContractForm
                key={`create-contract-${resetContractFormTrigger}`}
                onSubmit={handleCreateContractSubmit}
                onCancel={handleCloseCreateContract}
                isSubmitting={isCreatingContract}
                resetTrigger={resetContractFormTrigger}
                prefilledData={getPrefilledContractData(
                  selectedProposalForContract
                )}
                onRefreshList={async () => { }}
              />
            </ContractFormDataProvider>
          )}
        </div>
      </div>
    </ProposalFormDataProvider>
  );
};

export default Proposals;